import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';

interface PageRoute {
    path: string;
    name: string;
    description: string;
    icon: string;
    keywords: string[];
}

interface GlobalSearchProps {
    isVisible: boolean;
    onHide: () => void;
    initialQuery?: string;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isVisible, onHide, initialQuery = '' }) => {
    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);

    const routes: PageRoute[] = [
        {
            path: '/inicio',
            name: 'Inicio',
            description: 'Página principal del sistema',
            icon: 'fas fa-home',
            keywords: ['inicio', 'home', 'principal', 'dashboard']
        },
        {
            path: '/producto',
            name: 'Productos',
            description: 'Gestión de productos',
            icon: 'fas fa-cube',
            keywords: ['productos', 'articulos', 'items', 'catalogo']
        },
        {
            path: '/inventario',
            name: 'Inventario',
            description: 'Gestión de Inventario y Stock',
            icon: 'fas fa-warehouse',
            keywords: ['producto', 'inventario', 'stock', 'existencias']
        },
        {
            path: '/entrada',
            name: 'Entradas',
            description: 'Registro de entradas de mercancía',
            icon: 'fas fa-arrow-circle-up',
            keywords: ['entradas', 'ingresos', 'recepcion', 'mercancia']
        },
        {
            path: '/salida',
            name: 'Salidas',
            description: 'Registro de salidas de mercancía',
            icon: 'fas fa-arrow-circle-down',
            keywords: ['salidas', 'egresos', 'despacho', 'envios']
        },
        {
            path: '/mantenimiento',
            name: 'Mantenimiento',
            description: 'Gestión de mantenimientos',
            icon: 'fas fa-wrench',
            keywords: ['mantenimiento', 'reparaciones', 'servicios']
        },
        {
            path: '/peticionesadmin',
            name: 'Peticiones',
            description: 'Administración de peticiones',
            icon: 'fas fa-clipboard-check',
            keywords: ['peticiones', 'solicitudes', 'requests', 'admin']
        },
        {
            path: '/traspasos',
            name: 'Traspasos',
            description: 'Gestión de traspasos entre sucursales',
            icon: 'fas fa-random',
            keywords: ['traspasos', 'transferencias', 'movimientos', 'sucursales']
        },
        {
            path: '/empleadosAdmin',
            name: 'Empleados',
            description: 'Gestión de empleados',
            icon: 'fas fa-users',
            keywords: ['empleados', 'personal', 'trabajadores', 'staff']
        },
        {
            path: '/usuarios',
            name: 'Usuarios',
            description: 'Gestión de usuarios del sistema',
            icon: 'fas fa-users',
            keywords: ['usuarios', 'users', 'cuentas', 'accesos']
        },
        {
            path: '/sucursales',
            name: 'Sucursales',
            description: 'Gestión de sucursales',
            icon: 'fas fa-building',
            keywords: ['sucursales', 'tiendas', 'locales', 'branches']
        },
        {
            path: '/departamentos',
            name: 'Departamentos',
            description: 'Gestión de departamentos',
            icon: 'fas fa-sitemap',
            keywords: ['departamentos', 'areas', 'secciones', 'divisions']
        },
        {
            path: '/proveedores',
            name: 'Proveedores',
            description: 'Gestión de proveedores',
            icon: 'fas fa-address-book',
            keywords: ['proveedores', 'suppliers', 'vendors', 'distribuidores']
        },
        {
            path: '/marcas',
            name: 'Marcas',
            description: 'Gestión de marcas',
            icon: 'fas fa-plus-square',
            keywords: ['marcas', 'brands', 'etiquetas']
        },
        {
            path: '/lineas',
            name: 'Líneas',
            description: 'Gestión de líneas de productos',
            icon: 'fas fa-link',
            keywords: ['lineas', 'categorias', 'lines', 'grupos']
        },
        {
            path: '/sublineas',
            name: 'Sublíneas',
            description: 'Gestión de sublíneas de productos',
            icon: 'fas fa-bars',
            keywords: ['sublineas', 'subcategorias', 'sublines', 'subgrupos']
        },
        {
            path: '/unidades',
            name: 'Unidades',
            description: 'Gestión de unidades de medida',
            icon: 'fas fa-tags',
            keywords: ['unidades', 'medidas', 'units', 'medicion']
        },
        // === Nuevas rutas del sidebar ===
        {
            path: '/saldos',
            name: 'Saldos',
            description: 'Consulta de saldos históricos',
            icon: 'fas fa-history',
            keywords: ['saldos', 'historial', 'balances', 'stock']
        },
        {
            path: '/totales',
            name: 'Totales',
            description: 'Cálculo y visualización de totales',
            icon: 'fas fa-calculator',
            keywords: ['totales', 'resumen', 'cálculos', 'sumas']
        },
        {
            path: '/clicencias',
            name: 'Consultor Licencias',
            description: 'Consulta de licencias de software',
            icon: 'fas fa-unlock-alt',
            keywords: ['licencias', 'software', 'activaciones', 'claves']
        },
        {
            path: '/reporte-peticiones',
            name: 'Reportes de Peticiones',
            description: 'Generación de reportes de peticiones',
            icon: 'fas fa-chart-bar',
            keywords: ['reportes', 'peticiones', 'estadísticas', 'gráficos']
        },
        {
            path: '/consultor',
            name: 'Consultor',
            description: 'Herramienta de consulta avanzada',
            icon: 'fas fa-user-tie',
            keywords: ['consultor', 'búsqueda', 'experto', 'análisis']
        },
        {
            path: '/recientes',
            name: 'Movimientos Recientes',
            description: 'Lista de movimientos recientes',
            icon: 'fas fa-exchange-alt',
            keywords: ['recientes', 'últimos', 'movimientos', 'actividad']
        },
        {
            path: '/referencias',
            name: 'Visor de Facturas',
            description: 'Visualización de facturas y referencias',
            icon: 'fas fa-file-invoice',
            keywords: ['facturas', 'referencias', 'documentos', 'comprobantes']
        },
        {
            path: '/licencias',
            name: 'Licencias',
            description: 'Gestión de licencias del sistema',
            icon: 'fas fa-key',
            keywords: ['licencias', 'claves', 'activación', 'permisos']
        }
    ];

    // Filtrar rutas basado en la búsqueda
    const filteredRoutes = searchQuery.trim() === '' 
        ? routes 
        : routes.filter(route => {
            const query = searchQuery.toLowerCase();
            return (
                route.name.toLowerCase().includes(query) ||
                route.description.toLowerCase().includes(query) ||
                route.path.toLowerCase().includes(query) ||
                route.keywords.some(keyword => keyword.toLowerCase().includes(query))
            );
        });

    // Resetear índice seleccionado cuando cambie la búsqueda
    useEffect(() => {
        setSelectedIndex(0);
    }, [searchQuery]);

    // Enfocar input cuando se abra el modal
    useEffect(() => {
        if (isVisible && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isVisible]);

    // Establecer query inicial
    useEffect(() => {
        setSearchQuery(initialQuery);
    }, [initialQuery]);

    // Manejar navegación con teclado
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < filteredRoutes.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev > 0 ? prev - 1 : filteredRoutes.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (filteredRoutes[selectedIndex]) {
                    handleNavigation(filteredRoutes[selectedIndex].path);
                }
                break;
            case 'Escape':
                e.preventDefault();
                onHide();
                break;
        }
    }, [filteredRoutes, selectedIndex, onHide]);

    // Navegar a la ruta seleccionada
    const handleNavigation = useCallback((path: string) => {
        navigate(path);
        onHide();
        setSearchQuery('');
    }, [navigate, onHide]);

    // Manejar clic en una opción
    const handleOptionClick = useCallback((route: PageRoute) => {
        handleNavigation(route.path);
    }, [handleNavigation]);

    // Estilos CSS como objeto
    const styles = {
        globalSearchContainer: {
            background: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        } as React.CSSProperties,
        
        searchHeader: {
            padding: '20px 20px 16px 20px',
            borderBottom: '1px solid #e5e7eb',
            background: '#f9fafb'
        } as React.CSSProperties,
        
        searchInputContainer: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
        } as React.CSSProperties,
        
        searchIcon: {
            position: 'absolute',
            left: '12px',
            color: '#6b7280',
            zIndex: 1
        } as React.CSSProperties,
        
        searchShortcut: {
            position: 'absolute',
            right: '12px',
            background: '#e5e7eb',
            color: '#6b7280',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500
        } as React.CSSProperties,
        
        searchResults: {
            maxHeight: '400px',
            overflowY: 'auto' as const
        },
        
        searchResultItem: {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 20px',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            borderBottom: '1px solid #f3f4f6'
        } as React.CSSProperties,
        
        searchResultItemSelected: {
            background: '#eff6ff',
            borderLeft: '3px solid #3b82f6'
        } as React.CSSProperties,
        
        searchResultItemHover: {
            background: '#f3f4f6'
        } as React.CSSProperties,
        
        resultIcon: {
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f3f4f6',
            borderRadius: '8px',
            marginRight: '12px',
            color: '#6b7280',
            flexShrink: 0
        } as React.CSSProperties,
        
        resultIconSelected: {
            background: '#dbeafe',
            color: '#3b82f6'
        } as React.CSSProperties,
        
        resultContent: {
            flex: 1,
            minWidth: 0
        } as React.CSSProperties,
        
        resultName: {
            fontWeight: 600,
            color: '#111827',
            marginBottom: '2px'
        } as React.CSSProperties,
        
        resultDescription: {
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '2px'
        } as React.CSSProperties,
        
        resultPath: {
            fontSize: '12px',
            color: '#9ca3af',
            fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace'
        } as React.CSSProperties,
        
        resultShortcut: {
            width: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#6b7280',
            fontSize: '14px'
        } as React.CSSProperties,
        
        noResults: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            color: '#6b7280',
            textAlign: 'center' as const
        },
        
        noResultsIcon: {
            fontSize: '24px',
            marginBottom: '12px',
            opacity: 0.5
        } as React.CSSProperties,
        
        searchFooter: {
            padding: '12px 20px',
            background: '#f9fafb',
            borderTop: '1px solid #e5e7eb'
        } as React.CSSProperties,
        
        searchHelp: {
            display: 'flex',
            gap: '16px',
            fontSize: '12px',
            color: '#6b7280'
        } as React.CSSProperties,
        
        kbd: {
            background: '#e5e7eb',
            color: '#374151',
            padding: '2px 6px',
            borderRadius: '3px',
            fontFamily: 'inherit',
            fontSize: '11px',
            fontWeight: 500
        } as React.CSSProperties
    };

    return (
        <>
            <Dialog
                visible={isVisible}
                onHide={onHide}
                header={null}
                modal
                closable={false}
                style={{ 
                    width: '600px',
                    maxWidth: '90vw'
                }}
                contentStyle={{ 
                    padding: '0',
                    borderRadius: '8px',
                    overflow: 'hidden'
                }}
                maskStyle={{
                    backgroundColor: 'rgba(0, 0, 0, 0.3)'
                }}
            >
                <div style={styles.globalSearchContainer}>
                    {/* Header con input de búsqueda */}
                    <div style={styles.searchHeader}>
                        <div style={styles.searchInputContainer}>
                            <i className="fas fa-search" style={styles.searchIcon} />
                            <InputText
                                ref={inputRef}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Buscar páginas... (usa ↑↓ para navegar, Enter para ir)"
                                className="search-input"
                                autoComplete="off"
                                style={{
                                    width: '100%',
                                    padding: '12px 80px 12px 40px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    background: 'white',
                                    transition: 'all 0.2s ease'
                                }}
                            />
                            <div style={styles.searchShortcut}>
                                <span>ESC</span>
                            </div>
                        </div>
                    </div>

                    {/* Lista de resultados */}
                    <div style={styles.searchResults}>
                        {filteredRoutes.length > 0 ? (
                            filteredRoutes.slice(0, 8).map((route, index) => (
                                <div
                                    key={route.path}
                                    style={{
                                        ...styles.searchResultItem,
                                        ...(index === selectedIndex ? styles.searchResultItemSelected : {})
                                    }}
                                    onClick={() => handleOptionClick(route)}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                >
                                    <div style={{
                                        ...styles.resultIcon,
                                        ...(index === selectedIndex ? styles.resultIconSelected : {})
                                    }}>
                                        <i className={route.icon} />
                                    </div>
                                    <div style={styles.resultContent}>
                                        <div style={styles.resultName}>{route.name}</div>
                                        <div style={styles.resultDescription}>{route.description}</div>
                                        <div style={styles.resultPath}>{route.path}</div>
                                    </div>
                                    <div style={styles.resultShortcut}>
                                        {index === selectedIndex && <span>↵</span>}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={styles.noResults}>
                                <i className="fas fa-search" style={styles.noResultsIcon} />
                                <span>No se encontraron páginas para "{searchQuery}"</span>
                            </div>
                        )}
                    </div>

                    {/* Footer con ayuda */}
                    <div style={styles.searchFooter}>
                        <div style={styles.searchHelp}>
                            <span><span style={styles.kbd}>↑↓</span> navegar</span>
                            <span><span style={styles.kbd}>↵</span> ir a página</span>
                            <span><span style={styles.kbd}>ESC</span> cerrar</span>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* CSS adicional para el input focus */}
            <style>
                {`
                    .search-input:focus {
                        border-color: #3b82f6 !important;
                        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
                        outline: none !important;
                    }
                `}
            </style>
        </>
    );
};

export default GlobalSearch;